import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import JsBarcode from "jsbarcode";
import { api, formatDateShort } from "../lib/api";
import { Button } from "../components/ui/button";
import { Printer, ArrowLeft, Flame, Plus, Minus } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * Printable 100mm × 150mm label for sticking on the bath.
 * Big Code128 barcode of the tracking_code so Flowline's scanner / Giavani's
 * scanner can open the record instantly.
 */
export default function BarcodeLabelPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [error, setError] = useState(null);
  const [copies, setCopies] = useState(2);
  const barcodeRefs = useRef([]);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/bath-records/${id}`)
      .then((r) => { if (!cancelled) setRecord(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Couldn't load record"); });
    return () => { cancelled = true; };
  }, [id]);

  // Render barcodes once the record + copies count is known
  useEffect(() => {
    if (!record) return;
    barcodeRefs.current.forEach((el) => {
      if (!el) return;
      try {
        JsBarcode(el, record.tracking_code, {
          format: "CODE128",
          displayValue: true,
          fontSize: 22,
          height: 110,
          width: 3,
          margin: 4,
          background: "#ffffff",
          lineColor: "#000000",
          textMargin: 6,
          fontOptions: "bold",
          font: "monospace",
        });
      } catch {
        /* ignore */
      }
    });
  }, [record, copies]);

  const print = () => {
    setTimeout(() => window.print(), 50);
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="bg-white border border-zinc-200 rounded-md p-6 max-w-md">
          <h1 className="font-heading text-2xl font-extrabold mb-2">Couldn&apos;t load record</h1>
          <p className="text-sm text-zinc-600 mb-4">{error}</p>
          <Button onClick={() => navigate(-1)} variant="outline">Back</Button>
        </div>
      </div>
    );
  }
  if (!record) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-400">Loading…</div>;
  }

  const labels = Array.from({ length: Math.max(1, Math.min(20, copies)) });

  return (
    <div className="min-h-screen bg-zinc-100 print:bg-white">
      {/* Screen-only toolbar */}
      <div className="bg-white border-b border-zinc-200 shadow-sm print:hidden">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-3 flex flex-wrap items-center gap-3">
          <Button variant="ghost" onClick={() => navigate(`/bath/${id}`)} className="text-zinc-600 hover:text-zinc-900" data-testid="label-back">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to record
          </Button>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500 font-semibold">Copies</span>
            <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setCopies((c) => Math.max(1, c - 1))} data-testid="label-copies-down">
              <Minus className="h-3.5 w-3.5" />
            </Button>
            <span className="font-mono text-sm w-8 text-center" data-testid="label-copies">{copies}</span>
            <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setCopies((c) => Math.min(20, c + 1))} data-testid="label-copies-up">
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
          <Button onClick={print} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="label-print">
            <Printer className="h-4 w-4 mr-1.5" /> Print {copies} label{copies === 1 ? "" : "s"}
          </Button>
        </div>
        <div className="mx-auto max-w-5xl px-4 sm:px-6 pb-3 text-[11px] text-zinc-500">
          100&nbsp;mm × 150&nbsp;mm labels. In the print dialog set <span className="font-semibold">paper size = 100×150&nbsp;mm</span> (or &quot;Custom&quot;) and <span className="font-semibold">margins = None</span>. The Code128 barcode encodes the tracking code <span className="font-mono text-zinc-900">{record.tracking_code}</span>.
        </div>
      </div>

      {/* Labels */}
      <main className="mx-auto max-w-5xl px-4 sm:px-6 py-8 print:p-0 print:max-w-none">
        <div className="space-y-6 print:space-y-0">
          {labels.map((_, i) => (
            <div key={i} className="label-sheet bg-white shadow-sm print:shadow-none rounded-md print:rounded-none mx-auto" data-testid={`label-${i}`}>
              <div className="label-inner">
                {/* Header */}
                <div className="label-header">
                  <div className="label-brand">
                    <div className="label-brand-name">WeLove Bathrooms</div>
                    <div className="label-brand-tag">Flowline · scan to open record</div>
                  </div>
                  {record.is_urgent && (
                    <div className="label-urgent">
                      <Flame className="h-3 w-3" strokeWidth={3} /> URGENT
                    </div>
                  )}
                </div>

                {/* Body */}
                <div className="label-body">
                  <div className="label-row">
                    <span className="label-label">SKU</span>
                    <span className="label-value mono">{record.product_sku}</span>
                  </div>
                  {record.alternate_sku && (
                    <div className="label-row">
                      <span className="label-label">Alt</span>
                      <span className="label-value mono">{record.alternate_sku}</span>
                    </div>
                  )}
                  <div className="label-row">
                    <span className="label-label">{record.third_party_name ? `${record.third_party_name} PO` : "Giavani PO"}</span>
                    <span className="label-value mono">{record.internal_po || record.third_party_po || record.supplier_po || "—"}</span>
                  </div>
                  {record.customer_ref && (
                    <div className="label-row">
                      <span className="label-label">Customer</span>
                      <span className="label-value">{record.customer_ref}</span>
                    </div>
                  )}
                  {record.expected_return_date && (
                    <div className="label-row">
                      <span className="label-label">Back by</span>
                      <span className="label-value mono">{formatDateShort(record.expected_return_date)}</span>
                    </div>
                  )}
                </div>

                {/* Barcode */}
                <div className="label-barcode">
                  <svg ref={(el) => (barcodeRefs.current[i] = el)} />
                </div>

                {/* Footer */}
                <div className="label-footer">
                  <span className="label-foot-l">Scan with Flowline to open record</span>
                  <span className="label-foot-r mono">{record.tracking_code}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* Print CSS for 100×150mm labels */}
      <style>{`
        .label-sheet {
          width: 100mm;
          height: 150mm;
          padding: 4mm;
          box-sizing: border-box;
          border: 1px solid #e5e7eb;
        }
        .label-inner {
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        .label-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          border-bottom: 1.5px solid #000;
          padding-bottom: 2mm;
        }
        .label-brand-name {
          font-family: ui-sans-serif, system-ui, sans-serif;
          font-weight: 900;
          font-size: 16pt;
          line-height: 1;
          letter-spacing: -0.02em;
          color: #000;
        }
        .label-brand-tag {
          font-size: 7pt;
          color: #525252;
          margin-top: 1mm;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          font-weight: 600;
        }
        .label-urgent {
          display: inline-flex;
          align-items: center;
          gap: 1mm;
          background: #f59e0b;
          color: #fff;
          padding: 1mm 2mm;
          font-size: 8pt;
          font-weight: 800;
          letter-spacing: 0.1em;
          border-radius: 1mm;
        }
        .label-body {
          margin-top: 2.5mm;
          font-size: 9pt;
          color: #111;
          flex-shrink: 0;
        }
        .label-row {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          gap: 3mm;
          padding: 0.5mm 0;
          border-bottom: 0.4pt dashed #d4d4d8;
        }
        .label-row:last-child { border-bottom: none; }
        .label-label {
          font-size: 6.5pt;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #6b7280;
        }
        .label-value {
          font-size: 9pt;
          font-weight: 700;
          color: #000;
          text-align: right;
          max-width: 60mm;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .label-value.mono {
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        }
        .label-barcode {
          flex: 1 1 auto;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-top: 2mm;
        }
        .label-barcode svg {
          width: 100%;
          max-height: 100%;
        }
        .label-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-top: 1px solid #000;
          padding-top: 1mm;
          margin-top: 1mm;
          font-size: 6.5pt;
          color: #525252;
        }
        .label-foot-r {
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-weight: 700;
          color: #000;
        }
        @media print {
          @page {
            size: 100mm 150mm;
            margin: 0;
          }
          html, body { background: #fff !important; }
          .label-sheet {
            border: none !important;
            page-break-after: always;
            margin: 0 !important;
          }
          .label-sheet:last-child {
            page-break-after: auto;
          }
        }
      `}</style>
    </div>
  );
}
